<template>
  <v-card border flat>
    <v-toolbar color="surface">
      <v-toolbar-title class="font-weight-medium">
        {{ $t('fdbs.attributes.title') }}
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col class="align-self-center" cols="auto">
          <v-btn :readonly variant="outlined" @click="toggleInherit('sameAsBeforeOption')">
            {{ title('sameAsBeforeOption') }}
          </v-btn>
        </v-col>
        <v-col class="align-self-center">
          <v-switch
            v-model="attributes.sameAsBeforeOption"
            class="mt-0"
            :disabled="isInherited('sameAsBeforeOption')"
            :error-messages="errors.get('attributes.sameAsBeforeOption')"
            :label="$t('fdbs.attributes.sameAsBeforeOption')"
            name="attributes.sameAsBeforeOption"
            :readonly
            @update:model-value="errors.clear('attributes.sameAsBeforeOption')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="align-self-center" cols="auto">
          <v-btn :readonly variant="outlined" @click="toggleInherit('readyMealOption')">
            {{ title('readyMealOption') }}
          </v-btn>
        </v-col>
        <v-col class="align-self-center">
          <v-switch
            v-model="attributes.readyMealOption"
            class="mt-0"
            :disabled="isInherited('readyMealOption')"
            :error-messages="errors.get('attributes.readyMealOption')"
            :label="$t('fdbs.attributes.readyMealOption')"
            name="attributes.readyMealOption"
            :readonly
            @update:model-value="errors.clear('attributes.readyMealOption')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="align-self-center" cols="auto">
          <v-btn :readonly variant="outlined" @click="toggleInherit('reasonableAmount')">
            {{ title('reasonableAmount') }}
          </v-btn>
        </v-col>
        <v-col class="align-self-center">
          <v-text-field
            v-model.number="attributes.reasonableAmount"
            density="compact"
            :disabled="isInherited('reasonableAmount')"
            :error-messages="errors.get('attributes.reasonableAmount')"
            :label="$t('fdbs.attributes.reasonableAmount')"
            name="attributes.reasonableAmount"
            :readonly
            @update:model-value="errors.clear('attributes.reasonableAmount')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="align-self-center" cols="auto">
          <v-btn :readonly variant="outlined" @click="toggleInherit('useInRecipes')">
            {{ title('useInRecipes') }}
          </v-btn>
        </v-col>
        <v-col class="align-self-center" cols>
          <v-select
            v-model="attributes.useInRecipes"
            density="compact"
            :disabled="isInherited('useInRecipes')"
            :error-messages="errors.get('attributes.useInRecipes')"
            :items="useInRecipeTypeItems"
            :label="$t('fdbs.attributes.useInRecipes._')"
            name="attributes.useInRecipes"
            :readonly
            @update:model-value="errors.clear('attributes.useInRecipes')"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';

import type { ReturnUseErrors } from '@intake24/admin/composables/use-errors';
import type { InheritableAttributes, ResolvedInheritableAttributes } from '@intake24/common/types/http/admin';

import { computed } from 'vue';

import { useInRecipeTypes } from '@intake24/common/types';
import { useI18n } from '@intake24/ui';

type AttributeType = keyof InheritableAttributes;

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object as PropType<ReturnUseErrors>,
    required: true,
  },
  modelValue: {
    type: Object as PropType<InheritableAttributes>,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const defaultAttributes: ResolvedInheritableAttributes = {
  sameAsBeforeOption: false,
  readyMealOption: false,
  reasonableAmount: 0,
  useInRecipes: 0,
};

const { i18n } = useI18n();

const attributes = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit('update:modelValue', val);
  },
});

const useInRecipeTypeItems = computed(() =>
  Object.values(useInRecipeTypes).map(value => ({
    value,
    title: i18n.t(`fdbs.attributes.useInRecipes.${value}`),
  })),
);

function isInherited(attribute: AttributeType) {
  return attributes.value[attribute] === null;
};

function title(attribute: AttributeType): string {
  const key = attributes.value[attribute] === null ? 'override' : 'inherit';
  return i18n.t(`fdbs.attributes.${key}`);
};

function toggleInherit(attribute: AttributeType) {
  if (attributes.value[attribute] !== null) {
    attributes.value[attribute] = null;
    return;
  }

  // @ts-expect-error it doesn't narrow the type correctly
  attributes.value[attribute] = defaultAttributes[attribute];
};
</script>
